/**
 * client-logger.ts
 *
 * Phase B-1 of CrewSync's observability foundation. Single source of truth
 * for the browser-side ring buffers that the BugReportButton, ErrorBoundary,
 * and toast wiring all read from.
 *
 * Why a separate module (vs. keeping the buffers inside BugReportButton):
 *  - The ErrorBoundary needs to report errors BEFORE the bug button is
 *    mounted (e.g. a render crash on a page that doesn't include the
 *    button).
 *  - The fetch wrapper in api.ts needs to push request_ids without pulling
 *    in the React component tree.
 *  - When Chris files a bug, the button reads back the buffers; making them
 *    a module-level singleton means "what the user saw" and "what the
 *    button captures" are guaranteed to match.
 *
 * Buffer caps are tuned for "enough context to reproduce a bug" without
 * blowing the bug-report payload past Discord's 8 MB webhook limit:
 *   - 50 console/log entries
 *   - 30 network entries
 *   - 10 recent backend error responses
 */

const LOG_BUFFER_SIZE = 50;
const ERROR_BUFFER_SIZE = 10;

export type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

export interface LogEntry {
  ts: string;
  level: LogLevel;
  msg: string;
  route?: string;
  request_id?: string;
  meta?: Record<string, unknown>;
}

export interface RecentBackendError {
  ts: string;
  status: number;
  request_id?: string;
  error_code?: string;
  error_id?: string;
  category?: string;
  url: string;
  message_for_operator?: string;
}

// Module-level state. Survives across route changes.
const _logs: LogEntry[] = [];
const _errors: RecentBackendError[] = [];
let _lastRequestId: string | null = null;
let _lastErrorCode: string | null = null;
let _lastErrorId: string | null = null;
let _commitSha: string | null = null;
let _fetchCommitInFlight: Promise<string | null> | null = null;

function nowIso(): string {
  return new Date().toISOString();
}

function safeStringify(v: unknown): string {
  if (v instanceof Error) return `${v.name}: ${v.message}`;
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v); } catch { return String(v); }
}

function pushLog(entry: LogEntry) {
  _logs.push(entry);
  if (_logs.length > LOG_BUFFER_SIZE) _logs.splice(0, _logs.length - LOG_BUFFER_SIZE);
}

function pushError(entry: RecentBackendError) {
  _errors.push(entry);
  if (_errors.length > ERROR_BUFFER_SIZE) _errors.splice(0, _errors.length - ERROR_BUFFER_SIZE);
}

/** Add a log entry to the ring buffer at the given level. */
export function recordLog(level: LogLevel, msg: string, meta?: Record<string, unknown>) {
  pushLog({
    ts: nowIso(),
    level,
    msg: safeStringify(msg).slice(0, 500),
    route: typeof window !== 'undefined' ? window.location.pathname : undefined,
    meta,
  });
}

/** Read the most recent N log entries. Returns a copy — callers can mutate. */
export function getRecentLogs(limit: number = LOG_BUFFER_SIZE): LogEntry[] {
  if (limit >= _logs.length) return _logs.slice();
  return _logs.slice(-limit);
}

/** Record a backend error response (4xx/5xx). Used by api.ts wrapper. */
export function recordBackendError(entry: RecentBackendError) {
  pushError(entry);
  if (entry.request_id) _lastRequestId = entry.request_id;
  if (entry.error_code) _lastErrorCode = entry.error_code;
  if (entry.error_id) _lastErrorId = entry.error_id;
}

export function getRecentBackendErrors(limit: number = ERROR_BUFFER_SIZE): RecentBackendError[] {
  if (limit >= _errors.length) return _errors.slice();
  return _errors.slice(-limit);
}

export function getLastRequestId(): string | null { return _lastRequestId; }
export function getLastErrorCode(): string | null { return _lastErrorCode; }
export function getLastErrorId(): string | null { return _lastErrorId; }

/** Note the request_id from a successful response — keeps the "last seen" id current. */
export function noteRequestId(id: string | null | undefined) {
  if (typeof id === 'string' && id.length >= 8) _lastRequestId = id;
}

/**
 * Fetch the deployed commit sha from /api/system/version. Cached after first
 * resolution. Falls back to NEXT_PUBLIC_COMMIT_SHA env var if available.
 * Always returns; never throws.
 */
export async function getCommitSha(): Promise<string | null> {
  if (_commitSha) return _commitSha;
  // Pre-seed from env if Vercel injected it at build time. NEXT_PUBLIC_*
  // is the only way to get a non-server-only env into the bundle.
  const envSha = (typeof process !== 'undefined' && process.env && process.env.NEXT_PUBLIC_COMMIT_SHA) || null;
  if (envSha) {
    _commitSha = envSha;
    return _commitSha;
  }
  if (typeof window === 'undefined') return null;
  if (_fetchCommitInFlight) return _fetchCommitInFlight;
  const apiUrl = (typeof process !== 'undefined' && process.env && process.env.NEXT_PUBLIC_API_URL) || '';
  _fetchCommitInFlight = (async () => {
    try {
      const res = await fetch(`${apiUrl}/api/system/version`, { credentials: 'omit' });
      if (!res.ok) return null;
      const body = await res.json().catch(() => null);
      const sha = body && typeof body.commit === 'string' ? body.commit : null;
      _commitSha = sha;
      return sha;
    } catch {
      return null;
    } finally {
      _fetchCommitInFlight = null;
    }
  })();
  return _fetchCommitInFlight;
}

/**
 * Log a frontend-side error. Posts an envelope to the existing
 * /api/client-errors endpoint with the ring buffer + commit + request_id.
 * Fire-and-forget; never throws. Returns the response (or null on failure)
 * so callers can await if they want to.
 */
export async function logError(err: unknown, context?: { route?: string; error_code?: string; error_id?: string; tenant_id?: string }): Promise<Response | null> {
  if (typeof window === 'undefined') return null;
  const apiUrl = (typeof process !== 'undefined' && process.env && process.env.NEXT_PUBLIC_API_URL) || '';
  const errMsg = err instanceof Error ? `${err.name}: ${err.message}` : safeStringify(err);
  const stack = err instanceof Error ? (err.stack || '') : '';
  const code = context?.error_code || (err as { code?: string })?.code || _lastErrorCode || undefined;
  const errorId = context?.error_id || (err as { errorId?: string })?.errorId || _lastErrorId || undefined;
  recordLog('error', errMsg);
  const commitSha = await getCommitSha();
  const route = context?.route || (typeof window !== 'undefined' ? window.location.pathname : '/');

  // Map our log entries to the legacy console_logs shape so the existing
  // /api/client-errors endpoint accepts them without contract changes.
  const console_logs = _logs.map(l => ({ level: l.level, ts: l.ts, message: l.msg }));
  // network_logs reuses the most-recent backend errors, mapped to the
  // existing { url, method, status, duration_ms, ts, error } shape that
  // the bug-reports admin UI already renders. The ts is preserved.
  const network_logs = _errors.map(e => ({
    url: e.url,
    method: 'GET',
    status: e.status,
    duration_ms: 0,
    ts: e.ts,
    error: e.error_code,
  }));

  let res: Response | null = null;
  try {
    res = await fetch(`${apiUrl}/api/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // No auth header here on purpose — the route's `req.tenant` comes
      // from the cookie-based session; if the user isn't authenticated
      // the report is dropped and that's fine.
      credentials: 'include',
      body: JSON.stringify({
        message: errMsg.slice(0, 5000),
        stack: stack.slice(0, 8000),
        route,
        request_id: _lastRequestId || undefined,
        error_code: code,
        error_id: errorId,
        commit_sha: commitSha || undefined,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        viewport_width: typeof window !== 'undefined' ? window.innerWidth : undefined,
        viewport_height: typeof window !== 'undefined' ? window.innerHeight : undefined,
        console_logs,
        network_logs,
      }),
    });
  } catch {
    // Reporting must never crash the app. Swallow.
  }
  return res;
}

// Test-only seam: lets unit tests reset module state between cases.
// Not exported from the package's public surface — the underscore prefix
// signals "internal".
export function _resetForTests() {
  _logs.length = 0;
  _errors.length = 0;
  _lastRequestId = null;
  _lastErrorCode = null;
  _lastErrorId = null;
  _commitSha = null;
  _fetchCommitInFlight = null;
}
